import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { EnergyService } from '../services/energy-service';

import { forkJoin, Subscription, timer, fromEvent } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import { EnergyReadingModel } from '../models/energy-reading-model';
import { TemperatureService } from '../services/temperature.service';
import { TemperatureReadingModel } from '../models/tempread-reading-model';

import { SocketService } from '../services/socket-service';
import { ChartConfiguration, ChartType } from 'chart.js';


@Component({
    selector: 'app-energy',
    templateUrl: './energy.component.html',
    styleUrls: ['./energy.component.css'],
    providers: [EnergyService, TemperatureService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class EnergyComponent implements OnInit, OnDestroy {
  public lineChartData: ChartConfiguration['data'] = {
    datasets: [],
    labels: []
  };

  public lastEnergyReadingObj: EnergyReadingModel;
  public energyReadingsArr: EnergyReadingModel[];
  public tempReadingsArr: TemperatureReadingModel[];

  // Subscription object
  private sub: Subscription;
  private wsSub: Subscription;

  constructor(
    private energyService: EnergyService,
    private temperatureService: TemperatureService,
    private route: ActivatedRoute,
    private socketService: SocketService
  ) { }

  public lineChartOptions: any = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false
    },
    scales: {
      x: {
        ticks: {
          display: true,
          maxTicksLimit: 12
        }
      },
      ENERGY: {
        type: 'linear',
        position: 'left',
        ticks: {
          maxTicksLimit: 5
        }
      },
      B: {
        type: 'linear',
        position: 'right',
        display: true,
        ticks: {
          maxTicksLimit: 5
        }
      }
    },
    elements: {
      point: {
        radius: 0.2
      },
      line: {
        tension: 0.4
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false
      },
      crosshair: {
        line: {
          color: '#666',
          width: 1,
          dash: [5, 5]
        },
        snap: {
          enabled: true
        },
        zoom: {
          enabled: false
        }
      }
    }
  };

  public lineChartType : ChartType = 'line';

  // events
  public chartClicked(e: any): void {
  }

  public chartHovered(e: any): void {
  }


  ngOnInit() {
    this.sub = timer(0, 5 * 60 * 1000).subscribe(() => this.updateData());

    // Create websocket
    const ws = this.socketService.getWebsocket();
    const observable$ = fromEvent(ws, 'message'); // Receives message events once you subscribe to this observable.

    this.wsSub = observable$.subscribe(val => {
      const messageEventVal = <MessageEvent> val;
      const data = JSON.parse(messageEventVal.data);
      /*
      "{\"timestamp\":\"2020-09-01T22:28:00+02:00\",\"power\":633,\"accumulatedConsumption\":20.303596,\"accumulatedCost\":13.875694,\"currency\":\"SEK\",\"minPower\":408,\"averagePower\":903.6,\"maxPower\":5646,\"voltagePhase1\":232,\"voltagePhase2\":232,\"voltagePhase3\":233,\"currentL1\":0.62,\"currentL2\":2.13,\"currentL3\":0.95,\"lastMeterConsumption\":4826.5}"
      */
      this.lastEnergyReadingObj.deltaConsumption = data.power - this.lastEnergyReadingObj.currentConsumptionInWatts;
      this.lastEnergyReadingObj.currentConsumptionInWatts = data.power;
      this.lastEnergyReadingObj.sensorReadingDate = data.timestamp;
      this.lastEnergyReadingObj.accumulatedConsumption = data.accumulatedConsumption;
      //this.lastEnergyReadingObj.accumulatedCost = data.accumulatedCost;
      this.lastEnergyReadingObj.accumulatedCost = 0; // this.currentFixedEnergyPrice.totalPrice * data.accumulatedConsumption;
      this.lastEnergyReadingObj.accumulatedConsumptionLastHour = data.accumulatedConsumptionLastHour;
      this.lastEnergyReadingObj.voltageL1 = data.voltagePhase1;
      this.lastEnergyReadingObj.voltageL2 = data.voltagePhase2;
      this.lastEnergyReadingObj.voltageL3 = data.voltagePhase3;
      this.lastEnergyReadingObj.currentL1 = data.currentL1;
      this.lastEnergyReadingObj.currentL2 = data.currentL2;
      this.lastEnergyReadingObj.currentL3 = data.currentL3;
    });

  }

  ngOnDestroy() {
    if (this.sub !== undefined && this.sub.unsubscribe !== undefined) {
      this.sub.unsubscribe();
    }

    if (this.wsSub !== undefined && this.wsSub.unsubscribe !== undefined) {
      this.wsSub.unsubscribe();
    }

    this.socketService.getWebsocket().close();
  }

  private updateData() {

    forkJoin({
      latest: this.energyService.getLatest('10'),
      energy: this.energyService.getLastDay('10'),
      temperature: this.temperatureService.getLastDay('6')
    }).subscribe({
      next: ({ latest, energy, temperature }) => {
        this.lastEnergyReadingObj = latest;
        this.energyReadingsArr = energy;
        this.tempReadingsArr = temperature.sensorReadingsLst;
        this.fixlineChartData();
      },
      error: () => undefined
    });
  }

  private fixlineChartData() {
    const dataArrEnergy = new Array<number>();
    const dataArrTemp = new Array<number>();
    const labelArr = new Array<string>();

    if (this.energyReadingsArr != null) {
      for (const element of this.energyReadingsArr) {
        dataArrEnergy.push(element.currentConsumptionInWatts);
        labelArr.push(moment(element.sensorReadingDate).format('DD/MM HH:mm'));
      }
    }

    if (this.tempReadingsArr != null) {
      for (const element of this.tempReadingsArr) {
        dataArrTemp.push(element.sensorValue);
      }
    }

    const datasets: any[] = [];
    
    if (this.energyReadingsArr != null) {
      datasets.push({
        data: dataArrEnergy,
        label: 'Energy',
        yAxisID: 'ENERGY',
        fill: 'origin',
        backgroundColor: 'rgba(148,159,177,0.2)',
        borderColor: 'rgba(148,159,177,1)',
        pointBackgroundColor: 'rgba(148,159,177,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(148,159,177,0.8)'
      });
    }

    if (this.tempReadingsArr != null) {
      datasets.push({
        data: dataArrTemp,
        label: 'Temperature',
        yAxisID: 'B',
        fill: false,
        backgroundColor: 'rgba(77,83,96,0.2)',
        borderColor: 'rgba(77,83,96,1)',
        pointBackgroundColor: 'rgba(77,83,96,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(77,83,96,1)'
      });
    }

    this.lineChartData = {
      labels: labelArr,
      datasets: datasets
    };
  }

  public fillArray(value: number, len: number): number[] {
    const arr: number[] = [];

    for (let i = 0; i < len; i++) {
      arr.push(value);
    }

    return arr;
  }
}
